<template>
  <div class="login-page" data-testid="login-page">
    <div class="login-card-wrap">
      <el-card class="login-card" shadow="never">
        <template #header>
          <span class="login-title">系統登入</span>
        </template>
        <el-form
          ref="formRef"
          :model="form"
          :rules="rules"
          label-width="80px"
          label-position="top"
          @submit.prevent="handleLogin"
        >
          <el-form-item label="帳號" prop="UserId">
            <el-input
              id="login-field-userid"
              v-model="form.UserId"
              placeholder="請輸入帳號"
              size="large"
              clearable
              autofocus
              data-testid="login-input-userid"
            />
          </el-form-item>
          <el-form-item label="密碼" prop="Password">
            <el-input
              id="login-field-password"
              v-model="form.Password"
              type="password"
              placeholder="請輸入密碼"
              size="large"
              show-password
              data-testid="login-input-password"
              @keyup.enter="handleLogin"
            />
          </el-form-item>
          <el-form-item>
            <el-button
              id="login-btn-submit"
              type="primary"
              size="large"
              :loading="loading"
              class="login-btn"
              data-testid="login-btn-submit"
              @click="handleLogin"
            >
              登入
            </el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import axios from '@/api/axios'
import { logValidationError } from '@/api/frontendError'

const router = useRouter()
const route = useRoute()
const formRef = ref(null)
const loading = ref(false)

const form = reactive({
  UserId: '',
  Password: ''
})

const rules = {
  UserId: [{ required: true, message: '請輸入帳號', trigger: ['blur', 'change'] }],
  Password: [{ required: true, message: '請輸入密碼', trigger: ['blur', 'change'] }]
}

/** WebMonitor／自動化工具可能只寫入外層或原生 input，未觸發 el-input 的 v-model；驗證前自 DOM 同步。 */
function syncLoginFormFromDom() {
  const readUnder = (rootSel) => {
    const root = document.querySelector(rootSel)
    if (!root) return ''
    if (root.matches?.('input, textarea')) return String(root.value ?? '')
    const inner = root.querySelector?.('input, textarea')
    return inner && 'value' in inner ? String(inner.value ?? '') : ''
  }
  const uid =
    readUnder('#login-field-userid') ||
    readUnder('[data-testid="login-input-userid"]') ||
    readUnder('#login-field-userid input')
  const pwd =
    readUnder('#login-field-password') ||
    readUnder('[data-testid="login-input-password"]') ||
    readUnder('#login-field-password input')
  if (uid) form.UserId = uid
  if (pwd) form.Password = pwd
}

const handleLogin = async () => {
  if (!formRef.value) return
  syncLoginFormFromDom()
  await nextTick()
  try {
    await formRef.value.validate()
  } catch {
    ElMessage.error('請檢查表單欄位')
    logValidationError('表單驗證失敗：請檢查表單欄位', { component: 'Login' })
    return
  }
  loading.value = true
  try {
    const res = await axios.post('/users/validate', {
      UserId: form.UserId.trim(),
      Password: form.Password
    })
    const payload = res?.data ?? res
    const isValid = payload?.isValid === true || payload?.IsValid === true
    if (!isValid) {
      ElMessage.error('驗證失敗，請檢查帳號密碼')
      return
    }
    const userId = form.UserId.trim()
    const userName = payload?.userName ?? payload?.UserName ?? userId
    localStorage.setItem('token', userId)
    localStorage.setItem('userInfo', JSON.stringify({ userId, userName }))
    ElMessage.success('登入成功')
    let redirect = route.query.redirect ?? '/dashboard'
    if (Array.isArray(redirect)) redirect = redirect[0] ?? '/dashboard'
    if (typeof redirect !== 'string' || !redirect.trim()) redirect = '/dashboard'
    redirect = redirect.trim()
    try {
      if (redirect.includes('%')) redirect = decodeURIComponent(redirect)
    } catch (_) {
      /* 保持原字串 */
    }
    if (!redirect.startsWith('/')) redirect = `/${redirect.replace(/^\/+/, '')}`
    await router.push(redirect)
  } catch (err) {
    const msg = err.response?.data?.message ?? err.response?.data?.Message ?? err.message ?? '登入失敗'
    ElMessage.error(msg)
  } finally {
    loading.value = false
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $background-color-base;
}

.login-card-wrap {
  width: 100%;
  max-width: 400px;
  padding: 16px;
}

.login-card {
  .login-title {
    font-size: 20px;
    font-weight: bold;
    color: $text-color-primary;
  }

  .login-btn {
    width: 100%;
  }
}
</style>
